import { useRef, useEffect } from 'react';
import type { ExperimentConfig, SimulationState, ParameterValues } from '@axlabs/simulation-core';
import styles from './SimulationCanvas.module.css';

interface SimulationCanvasProps {
  config: ExperimentConfig;
  state: SimulationState | null;
  parameters: ParameterValues;
}

export function SimulationCanvas({ config, state, parameters }: SimulationCanvasProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !state) return;
    
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    // Get device pixel ratio for sharp rendering
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    
    // Set canvas size accounting for DPR
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    ctx.scale(dpr, dpr);
    
    // Clear canvas
    ctx.fillStyle = '#0d1117';
    ctx.fillRect(0, 0, rect.width, rect.height);
    
    // Draw based on experiment type
    switch (config.slug) {
      case 'simple-pendulum':
        drawPendulum(ctx, rect.width, rect.height, state, parameters);
        break;
      case 'projectile-motion':
        drawProjectile(ctx, rect.width, rect.height, state, parameters);
        break;
      case 'shm-spring':
        drawSpringMass(ctx, rect.width, rect.height, state, parameters);
        break;
      default:
        drawGenericState(ctx, rect.width, rect.height, state);
    }
  }, [config, state, parameters]);
  
  return (
    <div className={styles.container}>
      <canvas ref={canvasRef} className={styles.canvas} />
    </div>
  );
}

function drawPendulum(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  state: SimulationState,
  params: ParameterValues
) {
  const centerX = width / 2;
  const centerY = height * 0.2;
  const length = params['length'] ?? 1;
  
  // Scale: fit pendulum in canvas (max swing should fit)
  const scale = Math.min(width, height) * 0.35 / length;
  
  const theta = state.variables['theta'] ?? 0;
  const bobX = centerX + length * scale * Math.sin(theta);
  const bobY = centerY + length * scale * Math.cos(theta);
  
  // Draw pivot point
  ctx.fillStyle = '#484f58';
  ctx.beginPath();
  ctx.arc(centerX, centerY, 6, 0, Math.PI * 2);
  ctx.fill();
  
  // Draw rod
  ctx.strokeStyle = '#8b949e';
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(centerX, centerY);
  ctx.lineTo(bobX, bobY);
  ctx.stroke();
  
  // Draw bob
  const bobRadius = 12;
  ctx.fillStyle = '#58a6ff';
  ctx.beginPath();
  ctx.arc(bobX, bobY, bobRadius, 0, Math.PI * 2);
  ctx.fill();
  
  // Draw angle arc
  if (Math.abs(theta) > 0.05) {
    ctx.strokeStyle = 'rgba(88, 166, 255, 0.3)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    const arcRadius = 40;
    ctx.arc(centerX, centerY, arcRadius, Math.PI / 2, Math.PI / 2 + theta, theta < 0);
    ctx.stroke();
  }
  
  // Draw velocity indicator
  const omega = state.variables['omega'] ?? 0;
  if (Math.abs(omega) > 0.1) {
    const velScale = 20;
    // Velocity is tangent to the arc (perpendicular to rod)
    const velX = -length * scale * Math.cos(theta) * omega * velScale / 10;
    const velY = -length * scale * Math.sin(theta) * omega * velScale / 10;
    
    ctx.strokeStyle = '#56d364';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(bobX, bobY);
    ctx.lineTo(bobX + velX, bobY + velY);
    ctx.stroke();
    
    // Arrow head
    drawArrowHead(ctx, bobX + velX, bobY + velY, Math.atan2(velY, velX));
  }
  
  // Draw info text
  ctx.fillStyle = '#8b949e';
  ctx.font = '12px JetBrains Mono, monospace';
  ctx.fillText(`θ = ${(theta * 180 / Math.PI).toFixed(1)}°`, 10, 20);
  ctx.fillText(`ω = ${omega.toFixed(2)} rad/s`, 10, 36);
  ctx.fillText(`t = ${state.time.toFixed(2)} s`, 10, 52);
}

function drawProjectile(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  state: SimulationState,
  params: ParameterValues
) {
  const margin = 40;
  const groundY = height - margin;
  
  // Calculate scale based on expected range
  const v0 = params['initialVelocity'] ?? 20;
  const angle = (params['launchAngle'] ?? 45) * Math.PI / 180;
  const g = params['gravity'] ?? 9.81;
  const maxRange = (v0 * v0 * Math.sin(2 * angle)) / g * 1.2;
  const maxHeight = (v0 * v0 * Math.sin(angle) * Math.sin(angle)) / (2 * g) * 1.5;
  
  const scaleX = (width - 2 * margin) / Math.max(maxRange, 1);
  const scaleY = (height - 2 * margin) / Math.max(maxHeight, 1);
  
  const x = state.variables['x'] ?? 0;
  const y = state.variables['y'] ?? 0;
  
  const screenX = margin + x * scaleX;
  const screenY = groundY - y * scaleY;
  
  // Draw ground
  ctx.strokeStyle = '#484f58';
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(margin, groundY);
  ctx.lineTo(width - margin, groundY);
  ctx.stroke();
  
  // Draw projectile
  ctx.fillStyle = '#f0883e';
  ctx.beginPath();
  ctx.arc(screenX, screenY, 8, 0, Math.PI * 2);
  ctx.fill();
  
  // Draw velocity vector
  const vx = state.variables['vx'] ?? 0;
  const vy = state.variables['vy'] ?? 0;
  const velScale = 2;
  
  if (vx !== 0 || vy !== 0) {
    ctx.strokeStyle = '#56d364';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(screenX, screenY);
    ctx.lineTo(screenX + vx * velScale, screenY - vy * velScale);
    ctx.stroke();
    
    drawArrowHead(ctx, screenX + vx * velScale, screenY - vy * velScale, Math.atan2(-vy, vx));
  }
  
  // Draw info
  ctx.fillStyle = '#8b949e';
  ctx.font = '12px JetBrains Mono, monospace';
  ctx.fillText(`x = ${x.toFixed(1)} m`, 10, 20);
  ctx.fillText(`y = ${y.toFixed(1)} m`, 10, 36);
  ctx.fillText(`t = ${state.time.toFixed(2)} s`, 10, 52);
}

function drawSpringMass(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  state: SimulationState,
  _params: ParameterValues
) {
  const centerX = width / 2;
  const centerY = height / 2;
  const equilibriumX = centerX;
  
  const x = state.variables['x'] ?? 0;
  const scale = width * 0.15; // pixels per meter
  const massX = equilibriumX + x * scale;
  
  // Draw wall
  ctx.fillStyle = '#484f58';
  ctx.fillRect(50, centerY - 40, 10, 80);
  
  // Draw spring (zig-zag)
  const springStartX = 60;
  const springEndX = massX - 20;
  const coils = 8;
  const amplitude = 12;
  
  ctx.strokeStyle = '#8b949e';
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(springStartX, centerY);
  
  for (let i = 0; i <= coils; i++) {
    const t = i / coils;
    const px = springStartX + t * (springEndX - springStartX);
    const py = centerY + (i % 2 === 0 ? amplitude : -amplitude) * (i > 0 && i < coils ? 1 : 0);
    ctx.lineTo(px, py);
  }
  
  ctx.lineTo(massX - 20, centerY);
  ctx.stroke();
  
  // Draw mass
  const massSize = 40;
  ctx.fillStyle = '#58a6ff';
  ctx.fillRect(massX - massSize/2, centerY - massSize/2, massSize, massSize);
  
  // Draw equilibrium line
  ctx.strokeStyle = 'rgba(88, 166, 255, 0.3)';
  ctx.setLineDash([5, 5]);
  ctx.beginPath();
  ctx.moveTo(equilibriumX, centerY - 60);
  ctx.lineTo(equilibriumX, centerY + 60);
  ctx.stroke();
  ctx.setLineDash([]);
  
  // Draw displacement annotation
  if (Math.abs(x) > 0.01) {
    ctx.strokeStyle = '#f0883e';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(equilibriumX, centerY + 50);
    ctx.lineTo(massX, centerY + 50);
    ctx.stroke();
    
    // Arrow heads
    const dir = x > 0 ? 1 : -1;
    ctx.beginPath();
    ctx.moveTo(massX, centerY + 50);
    ctx.lineTo(massX - 5 * dir, centerY + 45);
    ctx.moveTo(massX, centerY + 50);
    ctx.lineTo(massX - 5 * dir, centerY + 55);
    ctx.stroke();
  }
  
  // Draw info
  ctx.fillStyle = '#8b949e';
  ctx.font = '12px JetBrains Mono, monospace';
  ctx.fillText(`x = ${x.toFixed(3)} m`, 10, 20);
  ctx.fillText(`v = ${(state.variables['v'] ?? 0).toFixed(3)} m/s`, 10, 36);
  ctx.fillText(`t = ${state.time.toFixed(2)} s`, 10, 52);
}

function drawGenericState(
  ctx: CanvasRenderingContext2D,
  _width: number,
  height: number,
  state: SimulationState
) {
  ctx.fillStyle = '#8b949e';
  ctx.font = '14px JetBrains Mono, monospace';
  
  let y = 30;
  ctx.fillText(`Time: ${state.time.toFixed(3)} s`, 20, y);
  y += 24;
  
  for (const [key, value] of Object.entries(state.variables)) {
    ctx.fillText(`${key}: ${value.toFixed(4)}`, 20, y);
    y += 24;
    if (y > height - 20) break;
  }
}

function drawArrowHead(ctx: CanvasRenderingContext2D, x: number, y: number, angle: number) {
  const size = 8;
  ctx.beginPath();
  ctx.moveTo(x, y);
  ctx.lineTo(x - size * Math.cos(angle - Math.PI/6), y - size * Math.sin(angle - Math.PI/6));
  ctx.moveTo(x, y);
  ctx.lineTo(x - size * Math.cos(angle + Math.PI/6), y - size * Math.sin(angle + Math.PI/6));
  ctx.stroke();
}
